import React, { FunctionComponent } from 'react'
import { Grid, Heading, Text, usePercent } from '@afterpaytouch/core'
import { Money } from '../Money'
import { useCheckoutSizes } from '../../utils/responsive'
import { useTranslation } from '../../utils/convergence'
import { useAppRouter, useCalculatedPaymentSchedules, useIsConsumerLendingOrder, useSelectedPaymentOption } from '../../state'
import styles from './style.module.css'

/**
 * "Summary" section of the redesigned Pay Monthly summary page (CASHDAP-3071, project: Dynamic
 * checkout unification 2026). Groups the regulated cost-of-credit disclosure into a dedicated
 * Summary block — purchase amount, interest (with APR), total amount payable, and amount due today
 * — matching the Pay in 4 / Bronte breakdown treatment. Copy + ordering per the FEB review slides
 * and Figma SOT frame 726:20432.
 *
 * Replaces the inline APR/interest/total list previously rendered by `ConsumerLendingPlanDetails`;
 * used only by the redesigned page (`summary-pay-monthly-redesign`). All figures come from the
 * selected `PaymentOption`.
 */
export const ConsumerLendingSummaryBreakdown: FunctionComponent = () => {
  const { t } = useTranslation()
  const { sectionHeadingSize, textSize } = useCheckoutSizes()
  const selectedPaymentOption = useSelectedPaymentOption()
  const isConsumerLending = useIsConsumerLendingOrder()
  const paymentSchedules = useCalculatedPaymentSchedules()
  const { locale } = useAppRouter()
  const { formatPercent } = usePercent({ locale, decimalPlaces: 2, shouldDivideBy100: true })

  if (selectedPaymentOption === undefined || !isConsumerLending) {
    return null
  }

  const { principalAmount, interestAmount, totalAmount, amountDueToday, apr } = selectedPaymentOption

  // "Due today (X of Y)" — the down payment is the first of the total scheduled payments. Y mirrors
  // the count rendered by ConsumerLendingPaymentSchedule (useCalculatedPaymentSchedules).
  // NOTE: confirm the (X of Y) counter semantics with design/legal before GA (flagged in the PR).
  const totalPayments = paymentSchedules.length

  return (
    <>
      <div className={styles.heading}>
        <Heading size={sectionHeadingSize} renderAs='h2'>
          {t('consumerLending:summary:breakdownTitle')}
        </Heading>
      </div>
      <Grid>
        <Text size={textSize} renderAs='span'>
          {t('consumerLending:summary:purchaseAmount')}
        </Text>
        <Grid.Item sm='auto' data-testid='clSummaryBreakdown-purchaseAmount'>
          <Money value={principalAmount} size={textSize} />
        </Grid.Item>
      </Grid>
      {interestAmount !== null && (
        <Grid>
          <Text size={textSize} renderAs='span'>
            {t('consumerLending:summary:interestWithApr', { apr: formatPercent(apr ?? 0) })}
          </Text>
          <Grid.Item sm='auto' data-testid='clSummaryBreakdown-interestAmount'>
            <Money value={interestAmount} size={textSize} />
          </Grid.Item>
        </Grid>
      )}
      <Grid>
        <Text size={textSize} renderAs='span'>
          {t('consumerLending:summary:totalAmountPayable')}
        </Text>
        <Grid.Item sm='auto' data-testid='clSummaryBreakdown-totalAmount'>
          <Money value={totalAmount} size={textSize} />
        </Grid.Item>
      </Grid>
      <Grid>
        <Text size={textSize} renderAs='span' bold>
          {t('consumerLending:summary:dueToday', { current: 1, total: totalPayments })}
        </Text>
        <Grid.Item sm='auto' data-testid='clSummaryBreakdown-amountDueToday'>
          <Money value={amountDueToday} size={textSize} bold />
        </Grid.Item>
      </Grid>
    </>
  )
}
